import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useCompanyStore } from '@/stores/company'
import { allowsPageAction, canEditPayrollRules, canFetchSalesProfit, resolvePageAccess } from '@/utils/rbac'
import { routes } from './routes'

const router = createRouter({
  history: createWebHistory(),
  routes,
})

const CHUNK_RELOAD_KEY = 'router_chunk_reload_attempt'

// Helper function to wait for auth with timeout
const waitForAuth = (authStore, timeoutMs = 10000) => {
  return new Promise((resolve) => {
    if (!authStore.authLoading) {
      resolve()
      return
    }

    const startTime = Date.now()
    const checkInterval = setInterval(() => {
      if (!authStore.authLoading || Date.now() - startTime >= timeoutMs) {
        clearInterval(checkInterval)
        if (authStore.authLoading) {
          console.warn('Router: Auth loading timeout - proceeding anyway')
        }
        resolve()
      }
    }, 50)
  })
}

// Helper function to wait for profile with timeout
const waitForProfile = (authStore, timeoutMs = 10000) => {
  return new Promise((resolve) => {
    if (!authStore.profileLoading) {
      resolve()
      return
    }

    const startTime = Date.now()
    const checkInterval = setInterval(() => {
      if (!authStore.profileLoading || Date.now() - startTime >= timeoutMs) {
        clearInterval(checkInterval)
        if (authStore.profileLoading) {
          console.warn('Router: Profile loading timeout - proceeding anyway')
        }
        resolve()
      }
    }, 50)
  })
}

// Navigation guards
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()
  const companyStore = useCompanyStore()

  // Wait for auth state with timeout to prevent infinite blocking
  await waitForAuth(authStore)

  // Load custom roles if authenticated but not yet loaded
  if (authStore.isAuthenticated && authStore.userProfile?.companyId && !companyStore.rolesLoaded) {
    try {
      await companyStore.loadCustomRoles()
    } catch (error) {
      console.warn('Failed to load roles in router guard:', error)
    }
  }

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
  const requiresRole = to.meta.requiresRole
  const requiresPage = to.meta.requiresPage
  const requiredEmail = to.meta.requiredEmail
  const payrollRules = to.matched.some((record) => record.meta.payrollRules)
  const salesProfit = to.matched.some((record) => record.meta.salesProfit)
  const payrollActor = () => ({
    email: authStore.userProfile?.email || authStore.user?.email,
    role: authStore.userProfile?.role,
  })
  const payrollRulesEditor = () => canEditPayrollRules(payrollActor(), authStore.customRoles)
  const salesProfitUser = () => canFetchSalesProfit(payrollActor(), authStore.customRoles)
  const canOpenPage = (pageId) => allowsPageAction(
    resolvePageAccess(authStore.userProfile?.role, pageId, authStore.customRoles),
    'retrieve',
  )

  if (requiresAuth && !authStore.isAuthenticated) {
    // Redirect to login if authentication is required
    next({
      path: '/login',
      query: { redirect: to.fullPath },
    })
  } else if (requiredEmail && String(authStore.userProfile?.email || authStore.user?.email || '').trim().toLowerCase() !== requiredEmail) {
    next({ path: '/attendance', query: { error: 'insufficient_permissions' } })
  } else if (salesProfit && !salesProfitUser()) {
    if (authStore.isAuthenticated && authStore.profileLoading) {
      await waitForProfile(authStore)
    }
    if (!salesProfitUser()) {
      next({ path: '/attendance', query: { error: 'insufficient_permissions' } })
    } else {
      next()
    }
  } else if (payrollRules && !payrollRulesEditor()) {
    if (authStore.isAuthenticated && authStore.profileLoading) {
      await waitForProfile(authStore)
    }
    if (!payrollRulesEditor()) {
      next({ path: '/attendance', query: { error: 'insufficient_permissions' } })
    } else {
      next()
    }
  } else if (requiresPage) {
    if (authStore.isAuthenticated && authStore.profileLoading) {
      await waitForProfile(authStore)
    }
    if (!canOpenPage(requiresPage)) {
      next({
        path: '/attendance',
        query: { error: 'insufficient_permissions' },
      })
    } else {
      next()
    }
  } else if (requiresRole && !authStore.isManager) {
    // If user is authenticated but profile might not be loaded yet, wait a bit
    if (authStore.isAuthenticated && authStore.profileLoading) {
      await waitForProfile(authStore)
    }

    // Check again after waiting
    if (requiresRole && !authStore.isManager) {
      // Redirect to attendance if role is required but user doesn't have it
      next({
        path: '/attendance',
        query: { error: 'insufficient_permissions' },
      })
    } else {
      next()
    }
  } else {
    next()
  }
})

router.afterEach(() => {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY)
  } catch (_) {
    // Ignore storage access issues.
  }
})

router.onError((error) => {
  const message = error?.message || ''
  const isChunkLoadFailure =
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /Loading chunk [\d]+ failed/i.test(message) ||
    /ChunkLoadError/i.test(message)

  if (!isChunkLoadFailure) {
    console.error('Router navigation error:', error)
    return
  }

  try {
    const currentPath = window.location.pathname + window.location.search
    const previousPath = sessionStorage.getItem(CHUNK_RELOAD_KEY)

    // Prevent infinite reload loops if recovery does not resolve the issue.
    if (previousPath === currentPath) {
      sessionStorage.removeItem(CHUNK_RELOAD_KEY)
      console.error('Chunk load recovery already attempted for this path:', currentPath)
      return
    }

    sessionStorage.setItem(CHUNK_RELOAD_KEY, currentPath)
    // Disabled auto-reload as it causes random refresh loops in some environments
    console.warn('Chunk load failure detected for:', currentPath)
    // window.location.reload()
  } catch (recoveryError) {
    console.error('Chunk load recovery failed:', recoveryError)
  }
})

export default router
